import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import logo from "../assets/logo/tivione-logo.jpg";
import { CONTACT_CONFIG } from "../config/contact";

const NAV_LINKS = [
  { label: "Zenders & Films", href: "/#films" },
  { label: "Sport", href: "/#sport" },
  { label: "Pakketten", href: "/#pricing" },
  { label: "Apparaten", href: "/#devices" },
  { label: "Gids", href: "/iptv-gids" },
  { label: "FAQ", href: "/#faq" },
];

function WhatsAppIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 448 512">
      <path
        fill="currentColor"
        d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222c0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222c0-59.3-25.2-115-67.1-157m-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4l-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2c0-101.7 82.8-184.5 184.6-184.5c49.3 0 95.6 19.2 130.4 54.1s56.2 81.2 56.1 130.5c0 101.8-84.9 184.6-186.6 184.6m101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18c-5.1-1.9-8.8-2.8-12.5 2.8s-14.3 18-17.6 21.8c-3.2 3.7-6.5 4.2-12 1.4c-32.6-16.3-54-29.1-75.5-66c-5.7-9.8 5.7-9.1 16.3-30.3c1.8-3.7.9-6.9-.5-9.7s-12.5-30.1-17.1-41.2c-4.5-10.8-9.1-9.3-12.5-9.5c-3.2-.2-6.9-.2-10.6-.2s-9.7 1.4-14.8 6.9c-5.1 5.6-19.4 19-19.4 46.3s19.9 53.7 22.6 57.4c2.8 3.7 39.1 59.7 94.8 83.8c35.2 15.2 49 16.5 66.6 13.9c10.7-1.6 32.8-13.4 37.4-26.4s4.6-24.1 3.2-26.4c-1.3-2.5-5-3.9-10.5-6.6"
      />
    </svg>
  );
}

export default function Header() {
  const { phoneNumber: PHONE_NUMBER, whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? "bg-surface-primary/90 backdrop-blur-md shadow-lg border-line"
            : "bg-surface-primary/70 backdrop-blur-sm border-transparent"
        }`}
      >
        <div className="w-[90%] sm:w-[90%] md:w-[85%] m-auto flex items-center justify-between py-3">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src={logo} alt="TiviOne logo" className="w-7 h-auto md:w-11 md:h-auto rounded-lg" />
            <span className="text-lg md:text-xl font-black tracking-tight text-white">
              TiviOne
            </span>
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((link) =>
              link.href.startsWith("/#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-semibold text-ink-secondary hover:text-accent transition-colors duration-200"
                >
                  {link.label}
                </a>
              ) : (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className="text-sm font-semibold text-ink-secondary hover:text-accent transition-colors duration-200"
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Desktop right side */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#25d366] hover:text-[#2fe27b] transition-colors duration-300"
            >
              <WhatsAppIcon className="w-[18px] h-[18px]" />
              <span className="text-sm font-medium">{PHONE_NUMBER}</span>
            </a>

            <a
              href="/#pricing"
              className="bg-gradient-button hover:brightness-110 text-white font-bold text-sm py-2.5 px-6 rounded-full transition-all duration-300 hover:scale-105 shadow-glow-cyan-sm"
            >
              BESTELLEN
            </a>
          </div>

          {/* Mobile right side */}
          <div className="lg:hidden flex items-center gap-2">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[#25d366] flex-shrink-0"
              aria-label={`WhatsApp: ${PHONE_NUMBER}`}
            >
              <WhatsAppIcon className="w-6 h-6" />
              <span className="text-[10px] font-semibold whitespace-nowrap">{PHONE_NUMBER}</span>
            </a>
            <a
              href="/#pricing"
              className="bg-gradient-button text-white font-bold text-[11px] py-2 px-3 rounded-full shadow-glow-cyan-sm whitespace-nowrap"
            >
              BESTELLEN
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Menu sluiten" : "Menu openen"}
              aria-expanded={menuOpen}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-line text-ink-secondary flex-shrink-0"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav panel */}
        <div
          className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out border-t bg-surface-primary ${
            menuOpen ? "max-h-[420px] opacity-100 border-line" : "max-h-0 opacity-0 border-transparent"
          }`}
        >
          <nav className="w-[90%] m-auto py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) =>
              link.href.startsWith("/#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="py-2.5 text-base font-semibold text-ink-secondary hover:text-accent border-b border-line last:border-b-0"
                >
                  {link.label}
                </a>
              ) : (
                <NavLink
                  key={link.href}
                  to={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="py-2.5 text-base font-semibold text-ink-secondary hover:text-accent border-b border-line last:border-b-0"
                >
                  {link.label}
                </NavLink>
              )
            )}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 bg-green-500/10 text-[#25d366] px-4 py-3 rounded-xl border border-green-500/25 font-semibold"
            >
              <MessageCircle className="w-4 h-4" />
              {PHONE_NUMBER}
            </a>
          </nav>
        </div>
      </header>

      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <div className="relative">
          <div className="absolute inset-0 bg-[#25d366] rounded-full animate-ping opacity-20"></div>
          <div
            className="absolute inset-0 bg-[#25d366] rounded-full animate-ping opacity-15"
            style={{ animationDelay: "0.5s" }}
          ></div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Contact via WhatsApp"
            className="relative bg-[#25d366] hover:bg-[#128C7E] text-white w-[4.5rem] h-[4.5rem] lg:w-20 lg:h-20 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.55)] hover:shadow-[0_8px_40px_rgba(37,211,102,0.75)] transition-all duration-300 hover:scale-110 border-4 border-white"
          >
            <WhatsAppIcon className="w-8 h-8 lg:w-9 lg:h-9" />
          </a>
        </div>
      </div>
    </>
  );
}
